import React, { useEffect, useRef, useState } from 'react';
import { CalendarBlank, CaretDown, X, Info } from '@phosphor-icons/react';

/**
 * Compact "Date ▾" pill that lives next to the Satellite/Hybrid/Roadmap/
 * Terrain switcher on the main map. Lets the user pick any Esri Wayback
 * release as the historical satellite imagery for the current project.
 *
 * Behaviour
 *  - Closed state shows either "Live" (no date saved) or the formatted ISO
 *    date of the saved release (e.g. "18 Nov 2024").
 *  - Opening the pill reveals a scrollable list of releases newest-first.
 *    Each row is the human date (already in `release.date` as YYYY-MM-DD).
 *  - First row is "Live (no date)" which clears the selection — i.e. revert
 *    to Google Maps base satellite imagery.
 *  - The pill is intentionally narrow and self-contained: it owns the open
 *    state and outside-click dismissal, but persistence + map-type wiring
 *    live in MapView via the `onChange` callback.
 *
 * Why a custom dropdown instead of <select>:
 *  - Native <select> can't render the "live" affordance, the close button on
 *    the active chip, or the scrollable hover styling consistently across
 *    browsers. The list is also only ~200 items so virtualization is not
 *    needed; a plain rendered <ul> with overflow-auto handles it fine.
 */
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Esri's Wayback imagery got a big resolution bump in 2018 — pre-2018
// releases were typically captured at a lower ground sample distance, so
// zooming in past ~zoom 18 shows visible up-scaling blur. We tag those
// entries in the dropdown so users understand the fuzz they see isn't a
// bug in the app.
function isLowResRelease(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getFullYear() < 2018;
}

// Shared explanation surfaced next to any "LOW RES" affordance. Kept as a
// constant so the badge on the pill and the info icon in the dropdown
// header always show identical wording.
const LOW_RES_EXPLAINER =
  'LOW RES = historical imagery captured before 2018 was recorded at a ' +
  'lower resolution. Zooming in beyond about level 18 may look blurry — ' +
  'this is a limitation of the source imagery, not an issue with the app.';

/**
 * Small hover/click tooltip wrapper.
 *
 * Behaviour:
 *  - Mouseenter shows the tooltip, mouseleave hides it (unless clicked
 *    to lock).
 *  - Clicking the trigger toggles a "locked" state — locked tooltips
 *    stay visible after the mouse leaves. Clicking anywhere outside
 *    the wrapper unlocks + hides.
 *  - This fixes the previous CSS-only `group-hover` implementation
 *    where clicking the trigger removed the hover state on some
 *    browsers/touch devices, dismissing the tooltip instead of the
 *    user's intuitive expectation (click → keep open).
 *
 * Rendered above the child via `absolute bottom-full`, kept out of
 * the layout flow so it doesn't push surrounding UI. Copy is capped
 * at max-w-[260px] so it never covers the whole dropdown.
 */
function HoverTip({ children, text, className = '' }) {
  const [hovered, setHovered] = useState(false);
  const [locked, setLocked] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!locked) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setLocked(false);
        setHovered(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [locked]);

  const visible = hovered || locked;
  return (
    <span
      ref={ref}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => { e.stopPropagation(); setLocked((v) => !v); }}
    >
      {children}
      <span
        role="tooltip"
        className={`pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 rounded bg-black/90 text-white text-[11px] normal-case tracking-normal leading-snug w-max max-w-[260px] whitespace-normal transition-opacity duration-150 z-[70] shadow-lg ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {text}
      </span>
    </span>
  );
}

export function ImageryDatePill({
  releases,
  value,         // { release_id, date } | null
  loading,
  disabled,
  panelCls,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  // Outside-click dismissal — keeps the pill behaving like a popover without
  // pulling in a full Dialog. We listen on mousedown so the close happens
  // before any click inside the dropdown can be eaten.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // When the menu opens, scroll the currently-selected option into view AND
  // focus it — otherwise the list defaults to the top (Live entry) and a
  // user with an older date selected has to scroll manually to find it.
  // Using `data-active` on the option instead of individual refs keeps the
  // JSX terse; scrollIntoView with block:'nearest' avoids jumpy scrolling
  // when the item is already visible.
  useEffect(() => {
    if (!open || !menuRef.current) return;
    // Defer to the next tick so the menu has painted at its final size
    // (scrollHeight only reflects the rendered layout after mount).
    const raf = requestAnimationFrame(() => {
      const el = menuRef.current?.querySelector('[data-active="true"]');
      if (el) {
        try { el.scrollIntoView({ block: 'nearest' }); } catch (_) { /* older browsers */ }
        try { el.focus({ preventScroll: true }); } catch (_) { /* focus can throw on hidden nodes */ }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const label = value?.date ? formatDate(value.date) : 'Live';
  const activeLowRes = isLowResRelease(value?.date);

  return (
    <div ref={rootRef} className="relative" data-testid="imagery-date-pill">
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-all inline-flex items-center gap-1.5 ${
          value?.release_id
            ? 'bg-primary text-white'
            : 'hover:bg-gray-100 dark:hover:bg-white/10 opacity-70 hover:opacity-100'
        } disabled:opacity-40 disabled:cursor-not-allowed`}
        title={value?.date ? `Historical imagery: ${formatDate(value.date)}` : 'Pick a historical imagery date'}
        data-testid="imagery-date-pill-trigger"
      >
        <CalendarBlank size={13} weight="bold" />
        <span>{loading ? '…' : label}</span>
        {activeLowRes && (
          <HoverTip text={LOW_RES_EXPLAINER}>
            <span
              className="ml-0.5 px-1 py-px rounded bg-amber-300/40 text-amber-100 text-[9px] font-semibold tracking-wider uppercase cursor-help"
              data-testid="imagery-date-pill-lowres-badge"
            >
              LOW RES
            </span>
          </HoverTip>
        )}
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {value?.release_id && !disabled && !loading && (
        <button
          type="button"
          onClick={() => onChange?.(null)}
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white inline-flex items-center justify-center shadow hover:scale-110 transition-transform"
          title="Clear — revert to live imagery"
          data-testid="imagery-date-pill-clear"
        >
          <X size={9} weight="bold" />
        </button>
      )}

      {open && (
        <div
          className={`absolute bottom-full mb-2 left-0 ${panelCls} rounded-md py-1 min-w-[200px] z-50 shadow-lg`}
          data-testid="imagery-date-pill-menu"
        >
          {/* Wayback zoom-cap notice. Kept OUTSIDE the scrollable list
              below so the LOW-RES info tooltip isn't clipped by the
              list's overflow container (an earlier version put both in
              the same overflow-y-auto div and the tooltip got cut off
              at the top of the dropdown in Firefox). */}
          <div
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider opacity-70 border-b border-border flex items-center gap-1.5"
            data-testid="imagery-date-pill-zoom-hint"
          >
            <span>Max zoom 20 · pre-2018 dates are lower res</span>
            <HoverTip text={LOW_RES_EXPLAINER}>
              <Info
                size={13}
                weight="fill"
                className="text-primary cursor-help shrink-0"
                data-testid="imagery-date-pill-lowres-info"
              />
            </HoverTip>
          </div>
          <div ref={menuRef} className="max-h-[280px] overflow-y-auto py-1">
          <button
            type="button"
            onClick={() => { onChange?.(null); setOpen(false); }}
            data-active={!value?.release_id ? 'true' : undefined}
            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-primary/10 transition-colors ${
              !value?.release_id ? 'font-bold text-primary bg-primary/5' : ''
            }`}
            data-testid="imagery-date-pill-option-live"
          >
            Live <span className="opacity-60">(today)</span>
          </button>
          <div className="border-t border-border my-1" />
          {releases.map((r) => {
            const active = value?.release_id === r.release_id;
            const lowRes = isLowResRelease(r.date);
            return (
              <button
                key={r.release_id}
                type="button"
                onClick={() => {
                  onChange?.({ release_id: r.release_id, date: r.date });
                  setOpen(false);
                }}
                data-active={active ? 'true' : undefined}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-primary/10 transition-colors flex items-center justify-between gap-2 ${
                  active ? 'font-bold text-primary bg-primary/5' : ''
                }`}
                data-testid={`imagery-date-pill-option-${r.release_id}`}
              >
                <span>{formatDate(r.date)}</span>
                {lowRes && (
                  <span
                    className="text-[9px] uppercase tracking-wider opacity-60"
                    aria-hidden="true"
                  >
                    LOW RES
                  </span>
                )}
              </button>
            );
          })}
          {releases.length === 0 && (
            <div className="px-3 py-2 text-xs opacity-60">No releases available</div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
